import { useEffect, useRef, useState } from "react";
import { Blog } from "types/blog";
import styles from "./styles.module.css";
import { theme } from "constants/theme";
import { CustomButtonSection } from "components/custom-button-section";
import moment from "moment";
import { CustomInput } from "components/custom-input";
import { fetchBlogs, uploadImage } from "services/blogService";
import { TagsInput } from "react-tag-input-component";
import EditorJS from "@editorjs/editorjs";
import Quote from "@editorjs/quote";
import ImageTool from "@editorjs/image";
import { CustomTextarea } from "components/custom-textarea";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";

type ImageKey =
  | "desktopPreviewImageUrl"
  | "desktopInnerImageUrl"
  | "mobilePreviewImageUrl"
  | "mobileInnerImageUrl";

const imageConfig: Record<ImageKey, { width: number; height: number }> = {
  desktopPreviewImageUrl: { width: 1202, height: 576 },
  desktopInnerImageUrl: { width: 3560, height: 930 },
  mobilePreviewImageUrl: { width: 748, height: 328 },
  mobileInnerImageUrl: { width: 860, height: 800 },
};

type BlogFormProps = {
  defaultImages?: boolean;
  existingBlog?: Blog | null;
  onSubmitMock: (blog: Blog) => void;
  previewBlogData?: Blog | null;
  previewBlog: boolean;
  setPreviewBlogData: (blog: Blog) => void;
  setPreviewBlog: (value: boolean) => void;
};

function slugify(title: string) {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/[\s_-]+/g, "-") // Replace spaces/underscores with hyphens
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
}

const ArticleForm = ({
  defaultImages = false,
  existingBlog = null,
  onSubmitMock,
  setPreviewBlogData,
  previewBlogData,
  setPreviewBlog,
  previewBlog,
}: BlogFormProps) => {
  const [title, setTitle] = useState(existingBlog?.title || "");
  const [description, setDescription] = useState(
    existingBlog?.description || ""
  );
  const [date, setDate] = useState(existingBlog?.date || new Date());
  const [desktopPreviewThumb, setDesktopPreviewThumb] = useState<string>(
    existingBlog?.desktopPreviewImageUrl || ""
  );
  const [desktopInnerThumb, setDesktopInnerThumb] = useState<string>(
    existingBlog?.desktopInnerImageUrl || ""
  );
  const [mobilePreviewThumb, setMobilePreviewThumb] = useState<string>(
    existingBlog?.mobilePreviewImageUrl || ""
  );
  const [mobileInnerThumb, setMobileInnerThumb] = useState<string>(
    existingBlog?.mobileInnerImageUrl || ""
  );
  const [bulletPoints, setBulletPoints] = useState<string[]>(
    existingBlog?.bulletPoints || []
  );
  const [formSaving, setFormSaving] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>();

  const desktopPreviewImageRef = useRef<HTMLInputElement>(null);
  const desktopInnerImageRef = useRef<HTMLInputElement>(null);
  const mobilePreviewImageRef = useRef<HTMLInputElement>(null);
  const mobileInnerImageRef = useRef<HTMLInputElement>(null);

  const [blogs, setBlogs] = useState<Blog[]>([]);

  useEffect(() => {
    fetchBlogs().then((data: Blog[]) => setBlogs(data));
  }, []);

  const ejInstance = useRef<EditorJS | null>(null);

  const initEditor = () => {
    const savedEditorContent = localStorage.getItem("editorContent");

    const editor = new EditorJS({
      holder: "editorjs",
      autofocus: true,
      placeholder: "Start writing your blog post here...",
      tools: {
        quote: Quote,
        image: {
          class: ImageTool,
          config: {
            uploader: {
              async uploadByFile(file: File) {
                const imageURL = await uploadImage(file);
                return {
                  success: 1,
                  file: {
                    url: imageURL,
                  },
                };
              },
            },
          },
        },
      },
      onChange: async () => {
        if (ejInstance.current) {
          const content = await ejInstance.current.save();
          localStorage.setItem("editorContent", JSON.stringify(content));
        }
      },
      data: savedEditorContent
        ? JSON.parse(savedEditorContent)
        : {
            time: new Date().getTime(),
            blocks: existingBlog?.customEditor?.blocks || [],
          },

      onReady() {
        ejInstance.current = editor;
      },
    });
  };

  useEffect(() => {
    if (!ejInstance.current && existingBlog) {
      initEditor();
    }

    return () => {
      ejInstance.current?.destroy();
      ejInstance.current = null;
    };
  }, [existingBlog]);

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: ImageKey,
    setPreview: (url: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;

    img.onload = () => {
      const { width: expectedWidth, height: expectedHeight } = imageConfig[key];
      if (img.width !== expectedWidth || img.height !== expectedHeight) {
        alert(
          `❌ Invalid dimensions for ${key}.\nExpected: ${expectedWidth} x ${expectedHeight},\nGot: ${img.width} x ${img.height}`
        );
        e.target.value = ""; // reset input
        return;
      }

      setPreview(url);
    };
  };

  useEffect(() => {
    if (!previewBlog && ejInstance.current && previewBlogData?.customEditor) {
      ejInstance.current.render(previewBlogData.customEditor);
    }
  }, [previewBlog]);

  const validate = () => {
    const errors: any = {};
    if (title === "") {
      errors.title = "Please enter title";
    }
    if (
      !existingBlog?.id &&
      blogs.some((item) => item.slug === slugify(title))
    ) {
      errors.title =
        "Entered title is already exists. Try with different title!";
    }

    const trimmedPoints = bulletPoints.map((point) => point.trim());
    if (trimmedPoints.some((point) => point === "")) {
      errors.bulletPoints = "Key words cannot be empty or just spaces";
    }
    setErrors(errors);
    return errors;
  };

  const handleSubmit = async (status: string) => {
    const errorObject = validate();
    if (Object.keys(errorObject).length > 0) {
      return;
    }
    setFormSaving(true);

    const deskTopPreviewFile = desktopPreviewImageRef.current?.files?.[0];
    const desktopInnerFile = desktopInnerImageRef.current?.files?.[0];
    const mobilePeviewFile = mobilePreviewImageRef.current?.files?.[0];
    const mobileInnerFile = mobileInnerImageRef.current?.files?.[0];

    const desktopPreviewImageUrl = deskTopPreviewFile
      ? await uploadImage(deskTopPreviewFile)
      : existingBlog?.desktopPreviewImageUrl || "";
    const desktopInnerImageUrl = desktopInnerFile
      ? await uploadImage(desktopInnerFile)
      : existingBlog?.desktopInnerImageUrl || "";
    const mobilePreviewImageUrl = mobilePeviewFile
      ? await uploadImage(mobilePeviewFile)
      : existingBlog?.mobilePreviewImageUrl || "";
    const mobileInnerImageUrl = mobileInnerFile
      ? await uploadImage(mobileInnerFile)
      : existingBlog?.mobileInnerImageUrl || "";

    if (!ejInstance.current) return;

    const editorOutput = await ejInstance.current.save();
    const blogData: Blog = {
      status,
      title,
      description,
      date,
      slug: slugify(title),
      desktopPreviewImageUrl,
      desktopInnerImageUrl,
      mobilePreviewImageUrl,
      mobileInnerImageUrl,
      bulletPoints: bulletPoints.filter((point) => point.trim() !== ""),
      customEditor: editorOutput, // ✅ saving Editor.js data in description field
      ...(existingBlog?.id && { id: existingBlog?.id }),
    };

    await onSubmitMock(blogData);
  };

  const handlePreviewBlog = async () => {
    if (!ejInstance.current) return;

    const editorOutputPreview = await ejInstance.current.save();

    if (editorOutputPreview) {
      setPreviewBlogData({
        id: existingBlog?.id,
        status: existingBlog?.status || "",
        title: title,
        slug: slugify(title),
        description: description,
        customEditor: editorOutputPreview,
        bulletPoints: bulletPoints.filter((point) => point.trim() !== ""),
        desktopPreviewImageUrl: desktopPreviewThumb,
        desktopInnerImageUrl: desktopInnerThumb,
        mobilePreviewImageUrl: mobilePreviewThumb,
        mobileInnerImageUrl: mobileInnerThumb,
        date: date,
      });

      setPreviewBlog(true);
    }
  };

  return (
    <>
      <form
        className={styles.formContainer}
        onSubmit={() => handleSubmit("publish")}
      >
        {!defaultImages && (
          <>
            <div>
              <CustomInput
                label="Title"
                name="title"
                placeholder="Enter title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={formSaving}
                style={{
                  color: "#000000",
                }}
              />
              {errors?.title && (
                <div className={styles.error}>{errors?.title}</div>
              )}
            </div>
            <CustomTextarea
              label="Short Description"
              name="description"
              placeholder="Write your blog description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={formSaving}
              rows={3}
              style={{
                color: "#000000",
              }}
            />
            <div className={styles.formGroup}>
              <label className={styles.label}>Blog Editor</label>
              <div
                id="editorjs"
                style={{
                  border: "1px solid #ccc",
                  padding: "16px",
                  borderRadius: "8px",
                }}
              ></div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Key Words:</label>
              <TagsInput
                disabled={formSaving}
                value={bulletPoints}
                onChange={setBulletPoints}
                name="bulletPoints"
                // placeHolder="Bulle"
              />
            </div>
            <div className={styles.imageBlock}>
              <div className={styles.dateInputblock}>
                <label htmlFor="date" className={styles.label}>
                  Date
                </label>
                <DatePicker
                  disabled={formSaving}
                  onChange={(value) => {
                    if (value && !Array.isArray(value)) {
                      setDate(moment(value as Date).format("YYYY-MM-DD"));
                    } else {
                      setDate("");
                    }
                  }}
                  value={date}
                  // calendarClassName="big-calendar"
                  className={`${styles.customDatePicker} ${styles.bigCalendar}`}
                />
                {errors?.date && (
                  <div className={styles.error}>{errors?.date}</div>
                )}
              </div>
              <div style={{ width: "100%" }}></div>
            </div>
          </>
        )}

        <div className={styles.imageContainer}>
          <div className={styles.imageBlock}>
            <div className={styles.imageUploadGroup}>
              <div style={{ display: "flex" }}>
                <label className={styles.label}>Desktop Preview Image</label>
                <label className={styles.greylabel}>
                  {`(width x height : ${imageConfig.desktopPreviewImageUrl.width} x ${imageConfig.desktopPreviewImageUrl.height})`}
                </label>
              </div>
              <input
                type="file"
                accept="image/*"
                disabled={formSaving}
                ref={desktopPreviewImageRef}
                className={styles.inputFile}
                onChange={(e) =>
                  handleImageChange(
                    e,
                    "desktopPreviewImageUrl",
                    setDesktopPreviewThumb
                  )
                }
              />
              {desktopPreviewThumb && (
                <img
                  src={desktopPreviewThumb}
                  alt="Desktop Preview"
                  className={styles.previewImage}
                />
              )}
            </div>
          </div>
          <div className={styles.imageBlock}>
            <div className={styles.imageUploadGroup}>
              <div style={{ display: "flex" }}>
                <label className={styles.label}>Desktop Inner Image</label>
                <label className={styles.greylabel}>
                  {`(width x height : ${imageConfig.desktopInnerImageUrl.width} x ${imageConfig.desktopInnerImageUrl.height})`}
                </label>
              </div>

              <input
                type="file"
                accept="image/*"
                disabled={formSaving}
                ref={desktopInnerImageRef}
                className={styles.inputFile}
                onChange={(e) =>
                  handleImageChange(
                    e,
                    "desktopInnerImageUrl",
                    setDesktopInnerThumb
                  )
                }
              />
              {desktopInnerThumb && (
                <img
                  src={desktopInnerThumb}
                  alt="Desktop Inner"
                  className={styles.previewImage}
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles.imageContainer}>
          <div className={styles.imageBlock}>
            <div className={styles.imageUploadGroup}>
              <div style={{ display: "flex" }}>
                <label className={styles.label}>Mobile Preview Image</label>
                <label className={styles.greylabel}>
                  {`(width x height : ${imageConfig.mobilePreviewImageUrl.width} x ${imageConfig.mobilePreviewImageUrl.height})`}
                </label>
              </div>
              <input
                type="file"
                accept="image/*"
                disabled={formSaving}
                ref={mobilePreviewImageRef}
                className={styles.inputFile}
                onChange={(e) =>
                  handleImageChange(
                    e,
                    "mobilePreviewImageUrl",
                    setMobilePreviewThumb
                  )
                }
              />
              {mobilePreviewThumb && (
                <img
                  src={mobilePreviewThumb}
                  alt="Mobile Preview"
                  className={styles.previewImage}
                />
              )}
            </div>
          </div>
          <div className={styles.imageBlock}>
            <div className={styles.imageUploadGroup}>
              <div style={{ display: "flex" }}>
                <label className={styles.label}>Mobile Inner Image</label>
                <label className={styles.greylabel}>
                   {`(width x height : ${imageConfig.mobileInnerImageUrl.width} x ${imageConfig.mobileInnerImageUrl.height})`}
                </label>
              </div>
              <input
                type="file"
                accept="image/*"
                disabled={formSaving}
                ref={mobileInnerImageRef}
                className={styles.inputFile}
                onChange={(e) =>
                  handleImageChange(
                    e,
                    "mobileInnerImageUrl",
                    setMobileInnerThumb
                  )
                }
              />
              {mobileInnerThumb && (
                <img
                  src={mobileInnerThumb}
                  alt="Mobile Inner"
                  className={styles.previewImage}
                />
              )}
            </div>
          </div>
        </div>
        {defaultImages ? (
          <div className={styles.buttonBlock}>
            <CustomButtonSection
              disabled={formSaving}
              text={formSaving ? "Saving..." : "Save"}
              colorScheme="green"
              onClick={() => handleSubmit("default-image")}
              style={{
                color: theme.colors.neutral[20],
                width: "180px",
                maxWidth: "180px",
                minWidth: "180px",
                height: "50px",
                marginTop: "20px",
                marginLeft: "10px",
              }}
            />
          </div>
        ) : (
          <div className={styles.buttonBlock}>
            <CustomButtonSection
              disabled={formSaving}
              text={"Preview"}
              colorScheme="green"
              onClick={() => handlePreviewBlog()}
              style={{
                color: theme.colors.neutral[20],
                width: "180px",
                maxWidth: "180px",
                minWidth: "180px",
                height: "50px",
                marginTop: "20px",
                marginLeft: "10px",
              }}
            />
            {!existingBlog?.id && (
              <CustomButtonSection
                type="button"
                disabled={formSaving}
                text={formSaving ? "Saving..." : "Save as Draft"}
                colorScheme="green"
                onClick={() => handleSubmit("draft")}
                style={{
                  color: theme.colors.neutral[20],
                  width: "180px",
                  maxWidth: "180px",
                  minWidth: "180px",
                  height: "50px",
                  marginTop: "20px",
                  marginLeft: "10px",
                }}
              />
            )}
            <CustomButtonSection
              disabled={formSaving}
              text={formSaving ? "Saving..." : "Publish"}
              colorScheme="green"
              onClick={() =>
                handleSubmit(
                  existingBlog?.status === "draft"
                    ? "publish"
                    : existingBlog?.status || "publish"
                )
              }
              style={{
                color: theme.colors.neutral[20],
                width: "180px",
                maxWidth: "180px",
                minWidth: "180px",
                height: "50px",
                marginTop: "20px",
                marginLeft: "10px",
              }}
            />
          </div>
        )}
        {defaultImages && (
          <div className={styles.formGroup} style={{ visibility: "hidden" }}>
            <label className={styles.label}>Blog Editor</label>
            <div
              id="editorjs"
              style={{
                border: "1px solid #ccc",
                padding: "16px",
                borderRadius: "8px",
              }}
            ></div>
          </div>
        )}
      </form>
    </>
  );
};

export default ArticleForm;
